import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import s from './Nav.module.scss';
import NavMenuItem from './components/NavMenuItem';
import SubMenu from './components/SubMenu';
import StoreList from './components/StoreList';
import AppDownloadArea from './components/AppDownloadArea';
import ThirdLevelNav from './components/ThirdLevelNav';
import { buildNavItems, getProductInfo, getAppDownloadLinks } from './utils/navUtils';

/**
 * PC端导航组件
 * @param {Object} props - 组件属性
 * @param {string} props.locale - 当前语言
 * @param {boolean} props.navPosition - 导航位置
 * @param {string} props.path - 当前路径
 */
const Nav = (props) => {
  const { locale, navPosition, path } = props;
  const qrcodeRef = useRef(null);
  const navRef = useRef(null);
  
  // 状态管理
  const [isHide, setIsHide] = useState(true);
  const [purchaseHide, setPurchaseHide] = useState(true);
  const [showShopList, setShowShopList] = useState(false);
  const [reload, setReload] = useState(true);
  
  // 路径解析
  const pathArr = path.split('/');
  
  // 处理导航显示样式
  let nav = s.nav;
  let airSecondSub = s.hide;
  let thirdSub = s.third;
  let thirdSubSpare = s.hide;
  
  // 处理应用下载页面
  let isAir = false;
  
  if (path === '/') {
    nav = s.navFixed;
    thirdSub = s.hide;
  }
  
  if (path === '/plus' || path === '/air') {
    thirdSub = s.hide;
    airSecondSub = s.airSecond;
    isAir = true;
  }
  
  if (path === '/lee-guitars/app') {
    thirdSub = s.hide;
    airSecondSub = s.airSecond;
  }
  
  // 构建导航项
  const { firstLevelItems, secondLevelItems, thirdLevelItems } = buildNavItems(null, pathArr, s);
  
  // 处理导航位置变化
  if (navPosition) {
    if (pathArr[1] === 'plus' || pathArr[1] === 'air') {
      airSecondSub = s.airSecondSubPosition;
    } else if (pathArr[1] !== 'mi-multifunction-air-monitor') {
      thirdSub = s.thirdSubPosition;
      thirdSubSpare = s.third;
    }
  }
  
  // 处理不显示三级导航的情况
  if (pathArr[1] === 'mi-multifunction-air-monitor' || pathArr[1] === 'mi-air-monitor' || pathArr[1] === 'store') {
    thirdSub = s.hide;
  }
  
  // 获取产品信息
  const { url, nameId } = getProductInfo(pathArr);
  
  // 处理背景和购买弹窗
  const bgName = !isHide ? s.bg : s.hide;
  const purchaseName = !purchaseHide ? s.purchaseShow : s.purchaseHide;
  
  // 获取下载链接
  const downloadLinks = getAppDownloadLinks(isAir);
  
  // 获取商店数据
  const goodLinksOrigin = localStorage.getItem('goodLinks');
  const goodsLink = goodLinksOrigin ? JSON.parse(goodLinksOrigin) : [];
  const goodsShops = goodsLink[pathArr[1]]?.shops || [];
  const isSoldOut = goodsLink[pathArr[1]]?.status === 'off' || false;
  const getJumpLink = goodsShops?.filter(item => item.tag) || [];
  const allMalls = goodsLink['all-mall']?.shops || [];
  
  // 处理二维码点击
  const handleQrCodeClick = (keyPress, e) => {
    e.stopPropagation();
    if (keyPress && qrcodeRef.current) {
      qrcodeRef.current.style.paddingBottom = '0px';
      qrcodeRef.current.style.paddingRight = '0px';
    } else if (qrcodeRef.current) {
      qrcodeRef.current.style.paddingBottom = '200px';
      qrcodeRef.current.style.paddingRight = '200px';
    }
    setIsHide(!isHide);
  };
  
  // 处理购买点击
  const handlePurchaseClick = (e) => {
    e.stopPropagation();
    setPurchaseHide(!purchaseHide);
  };
  
  // 滚动到顶部
  const toTop = () => {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  };

  // 监听goodLinks加载事件
  useEffect(() => {
    const handleGoodLinksLoaded = () => {
      setReload(prev => !prev); // 触发重新渲染
    };
    
    window.addEventListener('goodLinksLoaded', handleGoodLinksLoaded);
    
    return () => {
      window.removeEventListener('goodLinksLoaded', handleGoodLinksLoaded);
    };
  }, []);

  // 生成温湿度产品子菜单项
  const temperatureItems = [
    {
      path: '/temp-rh-monitor/overview',
      icon: s.goose,
      messageId: 'goose_lite',
      isActive: secondLevelItems[0],
      extraClass: s.limit_width
    },
    {
      path: '/temp-rh-monitor-3/overview',
      icon: s.goose,
      messageId: 'goose3_lite',
      isActive: secondLevelItems[16],
      extraClass: s.limit_width,
      new: true
    },
    // {
    //   path: '/temp-rh-monitor-lite/overview',
    //   icon: s.duck2,
    //   messageId: 'duck2',
    //   isActive: secondLevelItems[3],
    //   extraClass: s.limit_width
    // },
    {
      path: '/temp-rh-monitor-lite-3/overview',
      icon: s.goose,
      messageId: 'duck3',
      isActive: secondLevelItems[15],
      extraClass: s.limit_width
    },
    {
      path: '/temp-rh-monitor-pro-s/overview',
      icon: s.lora,
      messageId: 'pheasant',
      isActive: secondLevelItems[1],
      extraClass: s.limit_width
    },
    {
      path: '/temp-rh-monitor-pro-e/overview',
      icon: s.frog,
      messageId: 'frog',
      isActive: secondLevelItems[10],
      extraClass: s.limit_width,
      messageValues: { br: '' }
    },
    {
      path: '/wall-socket-thermometer/overview',
      icon: s.gecko,
      messageId: 'gecko_name',
      isActive: secondLevelItems[12],
      extraClass: s.limit_width
    },
    {
      path: '/lee-guitars-thermo-hygrometer/overview',
      icon: s.duck,
      messageId: 'magpie',
      isActive: secondLevelItems[2],
      extraClass: s.limit_width,
      messageValues: { br: '' }
    }
  ];
  
  // 生成时钟产品子菜单项
  const clockItems = [
    {
      path: '/bluetooth-alarm-clock/overview',
      icon: s.dove,
      messageId: 'dove',
      isActive: secondLevelItems[8],
      customStyle: { paddingLeft: 30, paddingRight: 30 }
    },
    {
      path: '/bluetooth-clock/overview',
      icon: s.chicken,
      messageId: 'chicken',
      isActive: secondLevelItems[9],
      customStyle: { paddingLeft: 30, paddingRight: 30 }
    }
  ];
  
  // 生成空气产品子菜单项
  const airItems = [
    {
      path: '/air-monitor-2/overview',
      icon: s.snow2,
      messageId: 'sw2',
      isActive: secondLevelItems[13],
      customStyle: { paddingLeft: 30, paddingRight: 30 }
    },
    {
      path: '/air-monitor-lite/overview',
      icon: s.dany,
      messageId: 'dany_name',
      isActive: secondLevelItems[5],
      customStyle: { paddingLeft: 30, paddingRight: 30 }
    },
    {
      path: '/co2-temp-rh-monitor/overview',
      icon: s.lora,
      messageId: 'ph_co2_name_value',
      isActive: secondLevelItems[11],
      customStyle: { paddingLeft: 30, paddingRight: 30 }
    }
  ];
  
  // 生成传感器产品子菜单项
  const sensorItems = [
    {
      path: '/door-window-sensor/overview',
      icon: s.hodor,
      messageId: 'hodor_name',
      isActive: secondLevelItems[6],
      customStyle: { paddingLeft: 30, paddingRight: 30 },
      messageValues: { br: ' ' }
    },
    {
      path: '/motion-light-sensor/overview',
      icon: s.parrot,
      messageId: 'parrot_name',
      isActive: secondLevelItems[7],
      customStyle: { paddingLeft: 30, paddingRight: 30 },
      messageValues: { br: ' ' }
    }
  ];

  return (
    <div className={nav} ref={navRef}>
      <div className={s.first_sub}>
        <ul
          className={s.items}
          onMouseLeave={() => {
            showShopList && setShowShopList(false);
          }}
        >
          {/* Logo */}
          <li>
            <div className={s.logo}>
              <NavLink to="/" className={firstLevelItems[0]}>
                <i />
              </NavLink>
            </div>
          </li>
          
          {/* 温湿度监测器 */}
          <li
            className={locale.indexOf('US') > -1 ? s.tempHum_en : s.tempHum}
            style={{ margin: 0, paddingLeft: 30, paddingRight: 30 }}
          >
            <span className={firstLevelItems[1]}>
              <FormattedMessage id="temp_rh" />
              <i className={s.line} />
            </span>
            <SubMenu
              items={temperatureItems}
              className={locale.indexOf('US') > -1 ? s.tempHumHide_en : s.tempHumHide}
              styles={s}
            />
          </li>
          
          {/* 时钟 */}
          <NavMenuItem
            messageId="clock"
            isActive={firstLevelItems[7]}
            className={locale.indexOf('US') > -1 ? s.airs_en : s.airs}
            styles={s}
          >
            <SubMenu
              items={clockItems}
              className={locale.indexOf('US') > -1 ? s.airHide_en : s.airHide}
              styles={s}
            />
          </NavMenuItem>
          
          {/* 空气监测器 */}
          <NavMenuItem
            messageId="air_name"
            isActive={firstLevelItems[2]}
            className={locale.indexOf('US') > -1 ? s.airs_en : s.airs}
            styles={s}
          >
            <SubMenu
              items={airItems}
              className={locale.indexOf('US') > -1 ? s.airHide_en : s.airHide}
              styles={s}
            />
          </NavMenuItem>
          
          {/* 传感器 */}
          <NavMenuItem
            messageId="sensor_name"
            isActive={firstLevelItems[8]}
            className={locale.indexOf('US') > -1 ? s.airs_en : s.airs}
            styles={s}
          >
            <SubMenu
              items={sensorItems}
              className={locale.indexOf('US') > -1 ? s.airHide_en : s.airHide}
              styles={s}
            />
          </NavMenuItem>
          
          {/* 蓝牙网关 */}
          <NavMenuItem
            linkTo="/bluetooth-gateway/overview"
            messageId="gateway"
            isActive={firstLevelItems[6]}
            styles={s}
          />
          
          {/* 青萍应用 */}
          <NavMenuItem
            linkTo="/plus"
            messageId="cg_app"
            isActive={firstLevelItems[3]}
            styles={s}
          />
          
          {/* 青萍IoT */}
          <li className={firstLevelItems[4]}>
            <span>
              <a
                href="https://qingpingiot.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FormattedMessage id="qing_ping_iot" />
              </a>
              <i className={s.line} />
            </span>
          </li>
          
          {/* 开发者 */}
          <li className={firstLevelItems[5]}>
            <span className={s.store}>
              <a
                href="https://developer.qingping.co/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FormattedMessage id="developer" />
              </a>
              <i className={s.line} />
            </span>
          </li>
          
          {/* 商城列表 */}
          <li
            className={firstLevelItems[5]}
            onMouseOver={() => setShowShopList(true)}
          >
            <span className={s.store}>
              <FormattedMessage id="store" />
              <i className={s.line} />
            </span>
          </li>
          
          <div
            className={s.shopArrow}
            onMouseOver={() => setShowShopList(true)}
          >
            <div style={{ height: 65, width: 28, marginTop: -46 }}></div>
            <StoreList
              stores={allMalls}
              isVisible={showShopList}
              locale={locale}
              onStoreClick={() => setReload(prev => !prev)}
              styles={s}
            />
          </div>
        </ul>
        
        {/* 购买按钮 */}
        <div className={s.buy}>
          <a
            href="https://item.mi.com/product/7303.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FormattedMessage id="buy" />
          </a>
        </div>
      </div>
      
      {/* 应用下载区域 */}
      <div className={airSecondSub}>
        <AppDownloadArea
          isAir={isAir}
          iosDownloadUrl={downloadLinks.ios}
          androidDownloadUrl={downloadLinks.android}
          googlePlayUrl={downloadLinks.googlePlay}
          locale={locale}
          showQrCode={!isHide}
          styles={s}
        />
      </div>
      
      {/* 三级导航 */}
      <div className={thirdSub}>
        <ThirdLevelNav
          url={url}
          productName={<FormattedMessage id={nameId} values={nameId === 'frog' || nameId === 'magpie' || nameId === 'hodor_name' || nameId === 'parrot_name' || nameId === 'chicken' ? { br: '' } : {}} />}
          activeItems={thirdLevelItems}
          onItemClick={toTop}
          isSoldOut={isSoldOut || !goodLinksOrigin || (!getJumpLink?.length && !goodsShops?.length)}
          getJumpLink={getJumpLink}
          goodsShops={goodsShops}
          buyText={<FormattedMessage id="buy" />}
          styles={s}
        />
      </div>
      <div className={thirdSubSpare}></div>
      
      {/* 隐藏reload状态的div */}
      <div style={{ height: 0, opacity: 0 }}>{Number(reload)}</div>
      
      {/* 购买提示 */}
      <div className={purchaseName}>
        <div className={s.purchaseBg} onClick={handlePurchaseClick} />
        <i />
        <span>
          请发送邮件至{' '}
          <a
            href="mailto:bd@qingping.co"
            target="_blank"
            rel="noopener noreferrer"
          >
            bd@qingping.co
          </a>
          ，<br />
          或致电李先生 189 1187 7866 获取报价。
        </span>
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => ({
  locale: state.language,
});

export default connect(mapStateToProps)(Nav); 